import { useTranslation } from 'react-i18next';

import * as St from './CookingApplOptions.styles';

import { SubsectionButtonOption } from '~/core/enums';
import { useCookingAppliances } from '~/core/hooks/useCookingAppliances';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { isCookingApplianceOptionEnabled } from '~/core/utils';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch } from '~/redux/hooks';
import {
  resetSelectedCookingApplianceId,
  resetSelectedCookingApplianceOption,
  updateSelectedBrowsingOption,
} from '~/redux/slices/cookingAppliance';
import RemoveAllSelectedProductsModal from '~organisms/RemoveAllSelectedProductsModal';

type CookingApplOptionsProps = {
  selectedCookingApplianceSectionOption?: SubsectionButtonOption;
};

const CookingApplOptions = ({
  selectedCookingApplianceSectionOption,
}: CookingApplOptionsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { retailerFileSettings } = useRetailerSettings();
  const options = Object.entries(SubsectionButtonOption).filter(
    ([, optionValue]) =>
      isCookingApplianceOptionEnabled(retailerFileSettings, optionValue)
  );

  const {
    getters: { drawingShapes, activeDrawingShape },
    handlers: { updateActiveDrawingShapeId },
  } = useDrawingShapeContext();

  const {
    removeAllCookingAppliances,
    totalCookingAppliancesAmount,
    setShowRemoveAllAppliancesModal,
    showRemoveAllAppliancesModal,
  } = useCookingAppliances();

  const onOptionSelected = (cookingApplianceOption: SubsectionButtonOption) => {
    if (
      cookingApplianceOption === SubsectionButtonOption.NONE &&
      totalCookingAppliancesAmount > 0
    ) {
      setShowRemoveAllAppliancesModal(true);

      return;
    }

    if (selectedCookingApplianceSectionOption === cookingApplianceOption) {
      dispatch(resetSelectedCookingApplianceOption());

      return;
    }

    dispatch(updateSelectedBrowsingOption(cookingApplianceOption));
    dispatch(resetSelectedCookingApplianceId());
  };

  const onConfirmRemoveAll = () => {
    removeAllCookingAppliances();
    dispatch(resetSelectedCookingApplianceOption());
    setShowRemoveAllAppliancesModal(false);
    updateActiveDrawingShapeId(activeDrawingShape?.id || drawingShapes[1].id);
  };

  return (
    <>
      <St.OptionsContainer>
        {options.map(([option, optionValue], index) => (
          <St.OptionButton
            key={index}
            data-is-selected={
              optionValue === selectedCookingApplianceSectionOption
            }
            onClick={() =>
              optionValue === selectedCookingApplianceSectionOption
                ? undefined
                : onOptionSelected(optionValue)
            }
          >
            {t(`SCROLLABLE_AREA.COOKINGAPPLIANCE.OPTIONS.${option}`)}
          </St.OptionButton>
        ))}
      </St.OptionsContainer>

      <RemoveAllSelectedProductsModal
        description={t(
          'SCROLLABLE_AREA.COOKINGAPPLIANCE.NO_COOKING_APPLIANCE_IN_SPACE_DESCRIPTION'
        )}
        isOpen={showRemoveAllAppliancesModal}
        title={t(
          'SCROLLABLE_AREA.COOKINGAPPLIANCE.NO_COOKING_APPLIANCE_IN_SPACE_TITLE'
        )}
        onClose={() => setShowRemoveAllAppliancesModal(false)}
        onConfirm={onConfirmRemoveAll}
      />
    </>
  );
};

export default CookingApplOptions;
